import {
    getTiktokAudioAssets,
    getTrndsClientSongTiktokAudioStats,
    TiktokAudioAsset,
    TrndsClientSongTiktokAudioStats,
} from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import debounce from 'lodash/debounce';

export default function useTopTiktokAudios({
    clientId,
    songId,
    page,
    search,
    ordering,
    page_size,
}: Parameters<typeof getTrndsClientSongTiktokAudioStats>[0]) {
    const [stats, setStats] = useState<TrndsClientSongTiktokAudioStats[]>([]);
    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorLoadingStats, setErrorLoadingStats] = useState(false);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setLoading(true);
                setErrorLoadingStats(false);
                const response = await getTrndsClientSongTiktokAudioStats(
                    {
                        clientId,
                        songId,
                        page,
                        search,
                        ordering,
                        page_size,
                    },
                    requestInit
                );

                if (response.status === 200) {
                    setStats(response.data.results);
                    setHasNextPage(Boolean(response.data.next));
                    setCount(response.data.count);
                    const audioIds = response.data.results.map((item) => item.audio_id);
                    const assets = await getTiktokAudioAssets(audioIds, requestInit);
                    setAssets(assets);
                    return;
                }

                setErrorLoadingStats(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setErrorLoadingStats(true);
            } finally {
                setLoading(false);
            }
        },
        [clientId, ordering, page, page_size, search, songId]
    );

    const fetchDebounced = useMemo(() => debounce(fetchData, 700), [fetchData]);
    useAbortableEffect(
        (signal) => {
            fetchDebounced({ signal });
        },
        [fetchDebounced]
    );

    return {
        count: count,
        data: stats.map((item) => {
            const asset = assets.find((asset) => asset.audio_id === item.audio_id);
            return {
                ...item,
                audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
                image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
                dailyGrowth: item.standardised_data.daily_video_counts.map((item) => ({
                    timestamp: item.timestamp,
                    value: item.change,
                })),
            };
        }),
        loading: loading,
        errorLoadingStats,
        hasNextPage,
        reset: () => {
            setStats([]);
            setAssets([]);
            setLoading(false);
            setErrorLoadingStats(false);
            setHasNextPage(false);
            setCount(0);
        },
    };
}
